import React, { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import './AlarmeVeiculoParado.css';

interface VeiculoEmAlerta {
  placa: string;
  motorista: string;
  tempoParado: number;
  inicioParada: string;
  operacao: string;
  telefones: string[];
  cva: string;
  sentidoRota?: string;
  confiancaSentido?: number;
  localizacao: {
    lat: number;
    lng: number;
    endereco: string;
  };
}

interface AlarmeVeiculoParadoProps {
  enabled: boolean; // Controla se o SOM do alarme está ativo (modal sempre fica ativo)
  onAlertaCountChange?: (count: number) => void; // Callback para notificar mudança na contagem
  forcarExibicao?: boolean; // Forçar exibição do modal manualmente
  onFecharModal?: () => void; // Callback quando fechar modal manualmente
  placasPermitidas?: string[];
  exibirModalAutomatico?: boolean;
}

const AlarmeVeiculoParado: React.FC<AlarmeVeiculoParadoProps> = ({ 
  enabled, 
  onAlertaCountChange, 
  forcarExibicao = false,
  onFecharModal,
  placasPermitidas,
  exibirModalAutomatico = true,
}) => {
  const [veiculosEmAlerta, setVeiculosEmAlerta] = useState<VeiculoEmAlerta[]>([]);
  const [veiculosSilenciados, setVeiculosSilenciados] = useState<Set<string>>(new Set());
  const [mostrarModal, setMostrarModal] = useState(false);
  const [filtroSentido, setFiltroSentido] = useState<'todos' | 'ida' | 'volta'>('todos');
  const [ordenacao, setOrdenacao] = useState<'maior_tempo' | 'menor_tempo' | 'placa'>('maior_tempo');
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(10);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const intervaloRef = useRef<NodeJS.Timeout | null>(null);

  // Função para buscar veículos parados há 5+ minutos
  const buscarVeiculosEmAlerta = async () => {
    try {
      const response = await axios.get('/api/monitoramento/veiculos-parados-alarme');
      if (response.data.success) {
        const placasPermitidasSet = placasPermitidas && placasPermitidas.length > 0
          ? new Set(placasPermitidas.map(placa => (placa || '').toUpperCase().trim()))
          : null;
        const veiculosAtuais = (response.data.veiculos || []).filter((veiculo: VeiculoEmAlerta) => {
          if (!placasPermitidasSet) {
            return true;
          }

          return placasPermitidasSet.has((veiculo.placa || '').toUpperCase().trim());
        });

        if (veiculosAtuais.length > 0) {
        
          // Notificar componente pai sobre a contagem total de alertas
          if (onAlertaCountChange) {
            onAlertaCountChange(veiculosAtuais.length);
          }
          
          // Criar Set com placas dos veículos que ainda estão em alerta
          const placasEmAlerta = new Set(veiculosAtuais.map((v: VeiculoEmAlerta) => v.placa));
          
          // Remover dos silenciados os veículos que não estão mais em alerta
          // (voltaram a andar ou entraram em Patio/Cliente)
          const novosSilenciados = new Set(
            Array.from(veiculosSilenciados).filter(placa => placasEmAlerta.has(placa))
          );
          
          // Atualizar lista de silenciados se houve mudanças
          if (novosSilenciados.size !== veiculosSilenciados.size) {
            setVeiculosSilenciados(novosSilenciados);
          }
          
          // Filtrar veículos que não foram silenciados
          const veiculosNaoSilenciados = veiculosAtuais.filter(
            (v: VeiculoEmAlerta) => !novosSilenciados.has(v.placa)
          );

          if (veiculosNaoSilenciados.length > 0) {
            setVeiculosEmAlerta(veiculosNaoSilenciados);
            setMostrarModal(exibirModalAutomatico || forcarExibicao);
            // Corrigido: nunca chama play() se enabled for false
            if (audioRef.current) {
              if (enabled && !forcarExibicao && exibirModalAutomatico) {
                if (audioRef.current.paused) {
                  audioRef.current.loop = true;
                  audioRef.current.play().catch(err => {
                    console.warn('Não foi possível tocar o alarme:', err);
                  });
                }
              } else {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
                audioRef.current.loop = false;
              }
            }
          } else {
            setVeiculosEmAlerta([]);
            setMostrarModal(false);
            pararAlarme();
          }
        } else {
          if (onAlertaCountChange) {
            onAlertaCountChange(0);
          }
          setVeiculosEmAlerta([]);
          setMostrarModal(false);
          setVeiculosSilenciados(new Set());
          pararAlarme();
        }
      } else {
        // Não há mais veículos em alerta - limpar tudo
        if (onAlertaCountChange) {
          onAlertaCountChange(0);
        }
        setVeiculosEmAlerta([]);
        setMostrarModal(false);
        setVeiculosSilenciados(new Set()); // Limpar silenciados
        pararAlarme();
      }
    } catch (error) {
      console.error('Erro ao buscar veículos em alerta:', error);
    }
  };

  // Parar alarme sonoro
  const pararAlarme = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.loop = false;
    }
  };

  // Silenciar alarme de um veículo específico
  const silenciarVeiculo = (placa: string) => {
    const novosSilenciados = new Set(veiculosSilenciados);
    novosSilenciados.add(placa);
    setVeiculosSilenciados(novosSilenciados);

    // Remover da lista de veículos em alerta
    const novosVeiculos = veiculosEmAlerta.filter(v => v.placa !== placa);
    setVeiculosEmAlerta(novosVeiculos);

    // Se não houver mais veículos, fechar modal e parar alarme
    if (novosVeiculos.length === 0) {
      setMostrarModal(false);
      pararAlarme();
    }
  };

  // Silenciar todos os veículos
  const silenciarTodos = () => {
    const novosSilenciados = new Set(veiculosSilenciados);
    veiculosEmAlerta.forEach(v => novosSilenciados.add(v.placa));
    setVeiculosSilenciados(novosSilenciados);
    setVeiculosEmAlerta([]);
    setMostrarModal(false);
    pararAlarme();
  };

  // Marcar veículo em manutenção

  const marcarManutencao = async (placa: string) => {
    try {
      await axios.post(`/api/monitoramento/manutencao/${placa}`, {
        em_manutencao: true
      });

      // Remover da lista de veículos em alerta
      const novosVeiculos = veiculosEmAlerta.filter(v => v.placa !== placa);
      setVeiculosEmAlerta(novosVeiculos);

      // Se não houver mais veículos, fechar modal e parar alarme
      if (novosVeiculos.length === 0) {
        setMostrarModal(false);
        pararAlarme();
      }

      // Notificar mudança
      if (onAlertaCountChange) {
        onAlertaCountChange(novosVeiculos.length);
      }
    } catch (error) {
      console.error('Erro ao marcar veículo em manutenção:', error);
      alert('Erro ao marcar veículo em manutenção');
    }
  };

  // Verificar a cada 30 segundos (sempre ativo, independente do enabled)
  useEffect(() => {
    buscarVeiculosEmAlerta(); // Verificar imediatamente
    
    intervaloRef.current = setInterval(() => {
      buscarVeiculosEmAlerta();
    }, 60000); // 1 minuto

    return () => {
      if (intervaloRef.current) {
        clearInterval(intervaloRef.current);
      }
      pararAlarme();
    };
  }, [veiculosSilenciados, placasPermitidas, exibirModalAutomatico, forcarExibicao]);

  // Responder à prop forcarExibicao
  useEffect(() => {
    if (forcarExibicao && veiculosEmAlerta.length > 0) {
      setMostrarModal(true);
      pararAlarme(); // Garante que o som não toque ao abrir modal forçado
    }
  }, [forcarExibicao, veiculosEmAlerta]);

  // Sempre que enabled mudar ou o modal abrir, parar o som se necessário
  useEffect(() => {
    if ((!enabled || !mostrarModal) && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.loop = false;
    }
  }, [enabled, mostrarModal]);

  // Função para fechar modal manualmente
  const fecharModal = () => {
    setMostrarModal(false);
    pararAlarme();
    if (onFecharModal) {
      onFecharModal();
    }
  };

  const normalizarSentido = (sentido?: string) => {
    const s = String(sentido || '').toLowerCase();
    if (s.includes('ida')) return 'ida';
    if (s.includes('volta')) return 'volta';
    return 'desconhecido';
  };

  const formatarTempoParado = (tempoMin: number) => {
    const min = Number(tempoMin || 0);
    const horas = Math.floor(min / 60);
    const minutos = min % 60;
    if (horas <= 0) return `${minutos}min`;
    return `${horas}h ${String(minutos).padStart(2, '0')}min`;
  };

  const toCoord = (value: any): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(String(value).replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  };

  const criarMapaPreview = (latRaw: any, lngRaw: any, endereco?: string): { url: string; tipo: 'iframe' } | null => {
    const lat = toCoord(latRaw);
    const lng = toCoord(lngRaw);

    if (lat !== null && lng !== null) {
      return {
        url: `https://maps.google.com/maps?q=${lat},${lng}&t=&z=13&ie=UTF8&iwloc=&output=embed`,
        tipo: 'iframe'
      };
    }

    if (endereco && String(endereco).trim()) {
      return {
        url: `https://maps.google.com/maps?q=${encodeURIComponent(String(endereco))}&t=&z=13&ie=UTF8&iwloc=&output=embed`,
        tipo: 'iframe'
      };
    }

    return null;
  };

  const criarLinkMaps = (latRaw: any, lngRaw: any, endereco?: string) => {
    const lat = toCoord(latRaw);
    const lng = toCoord(lngRaw);
    if (lat !== null && lng !== null) {
      return `https://www.google.com/maps?q=${lat},${lng}`;
    }
    if (endereco) {
      return `https://www.google.com/maps?q=${encodeURIComponent(endereco)}`;
    }
    return '#';
  };

  const totalIda = veiculosEmAlerta.filter((v) => normalizarSentido(v.sentidoRota) === 'ida').length;
  const totalVolta = veiculosEmAlerta.filter((v) => normalizarSentido(v.sentidoRota) === 'volta').length;

  const veiculosFiltradosOrdenados = useMemo(() => {
    let lista = [...veiculosEmAlerta];

    if (filtroSentido !== 'todos') {
      lista = lista.filter((v) => normalizarSentido(v.sentidoRota) === filtroSentido);
    }

    if (ordenacao === 'maior_tempo') {
      lista.sort((a, b) => (b.tempoParado || 0) - (a.tempoParado || 0));
    } else if (ordenacao === 'menor_tempo') {
      lista.sort((a, b) => (a.tempoParado || 0) - (b.tempoParado || 0));
    } else {
      lista.sort((a, b) => String(a.placa || '').localeCompare(String(b.placa || '')));
    }

    return lista;
  }, [veiculosEmAlerta, filtroSentido, ordenacao]);

  useEffect(() => {
    setPaginaAtual(1);
  }, [filtroSentido, ordenacao, itensPorPagina, veiculosFiltradosOrdenados.length]);

  const totalPaginas = Math.max(1, Math.ceil(veiculosFiltradosOrdenados.length / itensPorPagina));
  const paginaSegura = Math.min(Math.max(paginaAtual, 1), totalPaginas);
  const inicio = (paginaSegura - 1) * itensPorPagina;
  const fim = inicio + itensPorPagina;
  const veiculosPaginados = veiculosFiltradosOrdenados.slice(inicio, fim);

  // Limpar veículos silenciados quando eles voltarem a se mover
  // (serão removidos da lista automaticamente na próxima verificação)

  if (!mostrarModal || veiculosEmAlerta.length === 0) {
    return (
      <audio ref={audioRef} preload="auto">
        <source src="/sounds/alarm.mp3" type="audio/mpeg" />
        <source src="/sounds/alarm.ogg" type="audio/ogg" />
      </audio>
    );
  }

  return (
    <>
      <audio ref={audioRef} preload="auto">
        <source src="/sounds/alarm.mp3" type="audio/mpeg" />
        <source src="/sounds/alarm.ogg" type="audio/ogg" />
      </audio>

      <div className="alarme-overlay">
        <div className="alarme-modal">
          <div className="alarme-header">
            <div className="alarme-header-title-wrap">
              <h2>
                <span className="alarme-icon">🚨</span>
                ALERTA: Veículos Parados ({veiculosEmAlerta.length})
              </h2>
              <div className="alarme-header-metrics">
                <span className="alarme-chip alarme-chip-time">⏱ 5 min</span>
                <span className="alarme-chip alarme-chip-text">
                  {veiculosEmAlerta.length} veículo{veiculosEmAlerta.length > 1 ? 's' : ''} parado{veiculosEmAlerta.length > 1 ? 's' : ''} há 5 minutos ou mais
                </span>
              </div>
            </div>
            <div className="alarme-header-actions">
              <button
                className="alarme-minimize-btn"
                onClick={fecharModal}
                title="Fechar (sem silenciar)"
              >
                ✕ Fechar
              </button>
              <button
                className="alarme-close-btn"
                onClick={silenciarTodos}
                title="Silenciar todos os alertas"
              >
                🔕 Silenciar Todos
              </button>
            </div>
          </div>

          <div className="alarme-content">
            <div className="alarme-toolbar">
              <div className="alarme-filtros">
                <button className={`alarme-filter-btn ${filtroSentido === 'todos' ? 'active' : ''}`} onClick={() => setFiltroSentido('todos')}>
                  Todos ({veiculosEmAlerta.length})
                </button>
                <button className={`alarme-filter-btn ${filtroSentido === 'ida' ? 'active' : ''}`} onClick={() => setFiltroSentido('ida')}>
                  ➜ Ida ({totalIda})
                </button>
                <button className={`alarme-filter-btn ${filtroSentido === 'volta' ? 'active' : ''}`} onClick={() => setFiltroSentido('volta')}>
                  ➜ Volta ({totalVolta})
                </button>
              </div>

              <div className="alarme-order-wrap">
                <label htmlFor="alarme-order">Ordenar por:</label>
                <select id="alarme-order" value={ordenacao} onChange={(e) => setOrdenacao(e.target.value as 'maior_tempo' | 'menor_tempo' | 'placa')}>
                  <option value="maior_tempo">Maior tempo parado</option>
                  <option value="menor_tempo">Menor tempo parado</option>
                  <option value="placa">Placa (A-Z)</option>
                </select>
              </div>
            </div>

            <div className="alarme-veiculos-lista">
              {veiculosPaginados.map((veiculo) => {
                const sentido = normalizarSentido(veiculo.sentidoRota);
                const mapaPreview = criarMapaPreview(
                  veiculo.localizacao?.lat,
                  veiculo.localizacao?.lng,
                  veiculo.localizacao?.endereco
                );
                return (
                <div key={veiculo.placa} className="alarme-veiculo-card">
                  <div className="alarme-left">
                    <h3>{veiculo.placa}</h3>
                    <span className="alarme-tempo-badge">⏱ {formatarTempoParado(veiculo.tempoParado)}</span>
                    <div className="alarme-tempo-label">Tempo parado</div>
                    <div className="alarme-vehicle-icon">🚚<span>⚠</span></div>
                  </div>

                  <div className="alarme-main-info">
                    <div className="alarme-info-row">
                      <strong>👤 Motorista</strong>
                      <span>{veiculo.motorista || 'Sem informação'}</span>
                    </div>
                    <div className="alarme-info-row">
                      <strong>🪪 CVA</strong>
                      <span>{veiculo.cva || '-'}</span>
                    </div>
                    <div className="alarme-info-row">
                      <strong>💼 Operação</strong>
                      <span>{veiculo.operacao || '-'}</span>
                    </div>
                  </div>

                  <div className="alarme-location-info">
                    <div className="alarme-info-row">
                      <strong>➡ Sentido</strong>
                      <span className={`alarme-sentido-badge ${sentido === 'ida' ? 'ida' : sentido === 'volta' ? 'volta' : 'desconhecido'}`}>
                        {sentido === 'ida' ? 'Ida' : sentido === 'volta' ? 'Volta' : 'Desconhecido'}
                        {veiculo.confiancaSentido ? ` (${veiculo.confiancaSentido}%)` : ''}
                      </span>
                    </div>
                    <div className="alarme-info-row alarme-localizacao">
                      <strong>📍 Localização</strong>
                      <span>{veiculo.localizacao?.endereco || 'Sem localização'}</span>
                    </div>
                  </div>

                  <div className="alarme-map-box">
                    <div className="alarme-map-frame">
                      {mapaPreview ? (
                        <iframe
                          src={mapaPreview.url}
                          title={`Mapa ${veiculo.placa}`}
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                        />
                      ) : (
                        <div className="alarme-map-fallback">📍 Sem coordenadas</div>
                      )}
                    </div>
                    <a
                      className="alarme-map-open"
                      href={criarLinkMaps(veiculo.localizacao?.lat, veiculo.localizacao?.lng, veiculo.localizacao?.endereco)}
                      target="_blank"
                      rel="noreferrer"
                      title="Abrir no mapa"
                    >
                      ⛶
                    </a>
                  </div>

                  <div className="alarme-actions">
                    <button
                      className="alarme-manutencao-btn"
                      onClick={() => marcarManutencao(veiculo.placa)}
                      title="Marcar veículo como quebrado/em manutenção"
                    >
                      🔧 Veículo Quebrado
                    </button>
                    <button
                      className="alarme-silenciar-btn"
                      onClick={() => silenciarVeiculo(veiculo.placa)}
                    >
                      🔕 Silenciar Alarme
                    </button>
                  </div>
                </div>
              )})}
            </div>

            <div className="alarme-pagination">
              <div className="alarme-pagination-info">
                Mostrando {veiculosFiltradosOrdenados.length === 0 ? 0 : inicio + 1} a {Math.min(fim, veiculosFiltradosOrdenados.length)} de {veiculosFiltradosOrdenados.length} veículos
              </div>

              <div className="alarme-pagination-controls">
                <button onClick={() => setPaginaAtual(1)} disabled={paginaSegura === 1}>«</button>
                <button onClick={() => setPaginaAtual((p) => Math.max(1, p - 1))} disabled={paginaSegura === 1}>‹</button>
                <span>{paginaSegura}</span>
                <button onClick={() => setPaginaAtual((p) => Math.min(totalPaginas, p + 1))} disabled={paginaSegura === totalPaginas}>›</button>
                <button onClick={() => setPaginaAtual(totalPaginas)} disabled={paginaSegura === totalPaginas}>»</button>
              </div>

              <div className="alarme-pagination-size">
                <label htmlFor="alarme-page-size">Itens por página:</label>
                <select id="alarme-page-size" value={itensPorPagina} onChange={(e) => setItensPorPagina(Number(e.target.value))}>
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                </select>
              </div>
            </div>

            {veiculosEmAlerta.length > 1 && (
              <button 
                className="alarme-silenciar-todos-btn"
                onClick={silenciarTodos}
              >
                🔕 Silenciar Todos os Alarmes
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AlarmeVeiculoParado;
